/**
 * Readiness markers.
 *
 * `ReadinessDot` is the single visual atom for the four contract states. It is
 * deliberately NOT colour-only: every dot carries a glyph and a `title`, so the
 * state survives a colour-blind reader, a greyscale print and a dark theme.
 * Colours come from the antd theme tokens via `stateColors`.
 *
 * `ReadinessLegend` explains the four states; drop it under any tree that shows dots.
 *
 * NOTE for the integrator / the tree lane: the live hierarchy tree lives in
 * `src/pages/Batch-Processing/mbmr/ProcessHierarchy.js`, which this lane does not own.
 * To put dots on the tree rows, import from here and add one element next to the
 * operation name and the process name:
 *     import ReadinessDot from '../MBMRConfig/ReadinessDot';
 *     import { useReadiness } from '../MBMRConfig/ReadinessContext';
 *     const { index } = useReadiness() || {};
 *     <ReadinessDot state={index?.byOperation?.[operation.operation_id]?.state} />
 *     <ReadinessDot state={index?.byProcess?.[process.process_id]} />
 * Nothing else is needed — the provider already wraps the whole editor.
 */
import React from 'react';
import { useTheme } from '@/App';
import { STATES, safeState, stateColors } from './readinessModel';

export function ReadinessDot({ state, size = 10, showGlyph = true, style, className }) {
  const { isDark } = useTheme();
  const key = safeState(state);
  const meta = STATES[key];
  const { fg } = stateColors(key, isDark);
  const empty = key === 'not_started';

  return (
    <span
      role="img"
      aria-label={`Readiness: ${meta.label}`}
      title={`Readiness: ${meta.label}`}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size + 4,
        height: size + 4,
        borderRadius: '50%',
        flex: 'none',
        lineHeight: 1,
        fontSize: Math.max(7, size - 3),
        fontWeight: 700,
        color: empty ? fg : '#ffffff',
        backgroundColor: empty ? 'transparent' : fg,
        border: empty ? `1.5px solid ${fg}` : '1px solid transparent',
        ...style,
      }}
    >
      {showGlyph && !empty ? meta.glyph : ''}
    </span>
  );
}

export function ReadinessLegend({ compact = false, style }) {
  const { isDark } = useTheme();
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: compact ? 8 : 12,
        fontSize: 11,
        color: isDark ? '#94a3b8' : '#64748b',
        ...style,
      }}
    >
      {['ready', 'partial', 'blocked', 'not_started'].map((s) => (
        <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ReadinessDot state={s} size={9} />
          {STATES[s].short}
        </span>
      ))}
    </div>
  );
}

export default ReadinessDot;
